// Shows/updates a persistent local notification reflecting auto-sync progress,
// via the service worker already registered app-wide (see
// src/serviceWorkerRegistration.js) - this lets the sync status stay visible
// even while the tab/PWA is minimised or backgrounded. It cannot survive the
// browser being fully closed (there's no periodic background-sync
// registration, only an active page keeping useLocalAutoSync's timers alive),
// but minimising or switching tabs is fine.
//
// Deliberately NOT gated behind PushNotificationContext's subscription state -
// this is a *local* notification (no server push involved, no VAPID
// subscription needed), only requiring plain Notification permission,
// independent of whether the user has opted into push notifications for the
// rest of the app.
import { Capacitor } from '@capacitor/core';

const TAG_PREFIX = 'cm-auto-sync-';

function isSupported() {
  // Inside the native Android wrapper, FolderSyncForegroundService owns a
  // single persistent notification with real stats (folder name, pending
  // count, last poll time/result) - a strict superset of this heartbeat. On
  // that platform, `serviceWorker`/`Notification` may well exist in the
  // WebView too, so without this explicit check both would show up and
  // double-notify the same "still watching" fact - see nativeProvider.js's
  // module comment and useLocalAutoSync.js's startNativeService/notify.
  if (Capacitor.isNativePlatform()) return false;
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && typeof window.Notification !== 'undefined';
}

// `key` scopes the notification to one auto-sync instance (its storageKey -
// event id, or `${eventId}:${uploadLinkToken}`) so multiple armed folders in
// the same browser (e.g. the organiser's own EventPage tab and an upload-link
// tab) get their own notification rather than clobbering each other via `tag`.
// `signal`, if given, is re-checked right before actually showing the
// notification - callers that fire these off rapidly and unawaited (e.g. once
// per file during a folder scan) can build up a backlog of in-flight calls
// that individually can't be cancelled once dispatched; without this check,
// updates from before a scan was aborted keep landing (and re-opening the
// notification) for a while after the caller's own clearNotify() already
// closed it.
export async function updateAutoSyncNotification(key, { title, body, signal }) {
  if (!isSupported() || window.Notification.permission !== 'granted') return;
  if (signal?.aborted) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    if (signal?.aborted) return;
    await registration.showNotification(title, {
      body,
      tag: `${TAG_PREFIX}${key}`,
      // Closest thing to "non-dismissable" the Notification API offers - stays
      // visible until the user explicitly interacts with/closes it, rather
      // than auto-hiding after a few seconds like a normal notification.
      requireInteraction: true,
      // Progress updates fire frequently (every scan chunk/batch) - `silent`
      // avoids re-alerting (sound/vibration) on every single update; `tag` +
      // repeated showNotification() calls already replace the previous one
      // in place rather than stacking duplicates.
      silent: true,
    });
  } catch {
    // Best-effort only - a notification failure must never interrupt sync itself.
  }
}

export async function closeAutoSyncNotification(key) {
  if (!isSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const notifications = await registration.getNotifications({ tag: `${TAG_PREFIX}${key}` });
    notifications.forEach((n) => n.close());
  } catch {
    // Best-effort only.
  }
}
